import React from 'react'
import { useWeb3React } from '@web3-react/core'
import { Box, Flex, Text, LinkExternal, useTooltip } from 'uikit'
import styled from 'styled-components'
import useLocalDispatch from 'contexts/LocalRedux/useLocalDispatch'
import { useTranslation } from 'react-i18next'
import { REWARD_RATE } from 'state/predictions/config'
import { Bet, BetPosition } from 'state/types'
import { fetchLedgerData, markAsCollected } from 'state/predictions'
import { Result } from 'state/predictions/helpers'
import { useGetIsClaimable } from 'state/predictions/hooks'
import { getKCCExplorerLink } from 'utils'
import { multiplyPriceByAmount } from 'utils/prices'
import { useConfig } from 'pages/Predictions/context/ConfigProvider'
import { usePriceMjtUSDT } from 'state/hooks'

import useIsRefundable from '../../hooks/useIsRefundable'
import { formatMjt, getNetPayout } from './helpers'
import { CollectWinningsNormalButton as CollectWinningsButton } from '../CollectWinningsButton'
import PositionTag from '../PositionTag'
import ReclaimPositionButton from '../ReclaimPositionButton'

interface BetResultProps {
  bet: Bet
  result: Result
}

const StyledBetResult = styled(Box)`
  border: 1px solid rgba(115, 126, 141, 0.16);
  border-radius: 8px;
  margin-bottom: 24px;
  padding: 16px;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(115, 126, 141, 0.16);
  padding: 0 16px;
  box-sizing: content-box;
  margin: 12px -16px 12px 0;
  position: relative;
  left: -16px;
`

const Tag = styled(Flex)<{ isWin: boolean }>`
  background: ${({ isWin }) => (isWin ? '#14D89D' : '#F9479C')};
  justify-content: center;
  align-items: center;
  height: 24px;
  border-radius: 12px;
  padding: 0 5px;
`

const ImgTag = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 4px;
`

const DetailContainer = styled.div`
  background: white;
`

const ImgInfo = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 4px;
`

const Imgs = {
  win: require('assets/images/prediction/win.svg').default,
  lose: require('assets/images/prediction/lose.svg').default,
  info: require('assets/images/prediction/info.svg').default,
}

const BetResult: React.FC<BetResultProps> = ({ bet, result }) => {
  const { t } = useTranslation()
  const dispatch = useLocalDispatch()
  const { account, chainId } = useWeb3React()
  const { isRefundable } = useIsRefundable(bet.round.epoch)
  const canClaim = useGetIsClaimable(bet.round.epoch)
  const { payToken } = useConfig()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    <Text as="p" fontSize="14px">
      {t('PREDICTION_87')}
    </Text>,
    { placement: 'auto' }
  )

  const isWinner = result === Result.WIN

  // Winners get the payout, otherwise the claim what they put it if it was canceled
  const payout = isWinner ? getNetPayout(bet, REWARD_RATE) : bet.amount
  const mjtPrice = usePriceMjtUSDT()
  const totalPayout = multiplyPriceByAmount(mjtPrice, payout)
  const returned = payout + bet.amount

  // const getHeaderColor = () => {
  //   switch (result) {
  //     case Result.WIN:
  //       return 'warning'
  //     case Result.LOSE:
  //       return 'textRemark'
  //     case Result.CANCELED:
  //       return 'textDisabled'
  //     case Result.HOUSE:
  //       return 'textDisabled'
  //     default:
  //       return 'text'
  //   }
  // }

  const getHeaderText = () => {
    switch (result) {
      case Result.WIN:
        return t('PREDICTION_88')
      case Result.LOSE:
        return t('PREDICTION_89')
      case Result.CANCELED:
        return t('PREDICTION_41')
      case Result.HOUSE:
        return t('PREDICTION_27')
      default:
        return ''
    }
  }

  const getHeaderIcon = () => {
    switch (result) {
      case Result.WIN:
        return <ImgTag src={Imgs.win} />
      case Result.LOSE:
      case Result.CANCELED:
        return <ImgTag src={Imgs.lose} />
      default:
        return null
    }
  }

  const getResultColor = () => {
    switch (result) {
      case Result.WIN:
        return 'success'
      case Result.LOSE:
        return '#F9479C'
      case Result.CANCELED:
      case Result.HOUSE:
      default:
        return 'text'
    }
  }

  const handleSuccess = async () => {
    // We have to mark the bet as claimed immediately because it does not update fast enough
    dispatch(markAsCollected({ [bet.round.epoch]: true }))
    dispatch(fetchLedgerData({ account, epochs: [bet.round.epoch] }))
  }

  return (
    <DetailContainer>
      <Flex alignItems="center" justifyContent="space-between" mb="8px">
        {/* <Heading>{t('Your History')}</Heading> */}
        <Text bold fontSize="14px">
          {t('PREDICTION_90')}
        </Text>
        <Tag isWin={result === Result.WIN}>
          {getHeaderIcon()}

          <Text color="white" fontSize="14px" bold>
            {getHeaderText()}
          </Text>
        </Tag>
        {/* <Flex alignItems="center">
          <Heading as="h3" color={getHeaderColor()} textTransform="uppercase" bold mr="4px">
            {getHeaderText()}
          </Heading>
        </Flex> */}
      </Flex>
      <StyledBetResult>
        {result === Result.WIN && !canClaim && (
          <CollectWinningsButton hasClaimed={!canClaim} width="100%" mb="10px" onSuccess={handleSuccess}>
            {bet.claimed ? t('PREDICTION_91') : t('PREDICTION_32')}
          </CollectWinningsButton>
        )}
        {bet.claimed && bet.claimedHash && (
          <Flex justifyContent="center">
            <LinkExternal href={getKCCExplorerLink(chainId, bet.claimedHash, 'transaction')} mb="10px">
              {t('PREDICTION_16')}
            </LinkExternal>
          </Flex>
        )}
        {result === Result.CANCELED && isRefundable && (
          <ReclaimPositionButton epoch={bet.round.epoch} width="100%" mb="10px" />
        )}
        <Flex alignItems="center" justifyContent="space-between" mb="10px">
          <Text fontSize="14px" color="textRemark">
            {t('PREDICTION_92')}:
          </Text>
          <PositionTag betPosition={bet.position}>
            {bet.position === BetPosition.BULL ? t('PREDICTION_10') : t('PREDICTION_11')}
          </PositionTag>
        </Flex>
        <Flex alignItems="center" justifyContent="space-between" mb="10px">
          <Text fontSize="14px" color="textRemark">
            {t('PREDICTION_93')}
          </Text>
          <Text fontSize="14px">{`${formatMjt(bet.amount)} ${payToken.symbol}`}</Text>
        </Flex>
        <Flex alignItems="start" justifyContent="space-between">
          <Text fontSize="14px" color="textRemark">
            {isWinner ? t('PREDICTION_94') : t('PREDICTION_78')}:
          </Text>
          <Box style={{ textAlign: 'right' }}>
            <Text bold fontSize="14px" color={getResultColor()}>{`${
              result === Result.CANCELED ? '' : isWinner ? '+' : '-'
            }${formatMjt(payout)} ${payToken.symbol}`}</Text>
            <Text fontSize="12px" color="textRemark">
              {`~$${totalPayout.toFixed(2)}`}
            </Text>
          </Box>
        </Flex>
        {isWinner && (
          <>
            <Divider />
            <Flex alignItems="start" justifyContent="space-between">
              <Text fontSize="14px" color="textRemark">
                {t('PREDICTION_95')}:
              </Text>
              <Flex justifyContent="end" alignItems="center">
                <Text fontSize="14px">{`${formatMjt(returned)} ${payToken.symbol}`}</Text>
                <span ref={targetRef}>
                  <ImgInfo src={Imgs.info} />
                </span>
              </Flex>
              {tooltipVisible && tooltip}
            </Flex>
          </>
        )}
      </StyledBetResult>
    </DetailContainer>
  )
}

export default BetResult
